import { DomainError, NetworkError, NotExistError, RequestTimeout, ServerError } from 'scripts/application/lib/error';
import i18n from 'scripts/i18n/i18n';
import { isHoldingQuestions } from 'actions/team_assist_v2/ask_team_assist_v2';
import handleTeamAssistV2Error from 'actions/team_assist_v2/lib/handle_team_assist_v2_error';
import { finishRequest, startRequest } from 'actions/team_assist_v2/lib/in_flight_team_assist_v2_requests';
import isTeamAssistV2Enabled from 'actions/team_assist_v2/lib/is_team_assist_v2_enabled';
import StreamingTeamAssistV2FollowUp from 'actions/team_assist_v2/lib/streaming_team_assist_v2_follow_up';
import {
  proposalStatus,
  TeamAssistV2ErrorCode,
  TeamAssistV2ProposalDecision,
  TeamAssistV2ProposalStatus,
} from 'actions/team_assist_v2/lib/team_assist_v2_answer';
import { findProposalBlock } from 'actions/team_assist_v2/lib/team_assist_v2_proposals';
import RequestTeamAssistV2 from 'actions/team_assist_v2/request_team_assist_v2';
import ShowToast from 'actions/toast_deprecated/show_toast';
import { ToastType } from 'models/toast_deprecated';

/**
 * Runs a pending proposed action with the agent's `input` (the model's
 * arguments, possibly edited), and streams the answer that follows into the
 * same turn. Waits, as a question does, while anything streams or the session
 * loads or clears. A run glados refuses because of the input or the app is
 * the agent's to try again, and the card says why; one it refuses because the
 * proposal was decided or is gone loads the session again.
 */
export default class RunTeamAssistV2Proposal {
  constructor(context) {
    this.context = context;
  }

  run({ customerId, turnId, toolUseId, input }) {
    if (!isTeamAssistV2Enabled(this.context.stores.appFeatures.get())) {
      return;
    }
    if (!customerId || !this.context.stores.customers.has({ id: customerId })) {
      return;
    }
    const store = this.context.stores.customers.storesFor(customerId).teamAssistsV2;
    if (isHoldingQuestions(store) || !store.has({ id: turnId })) {
      return;
    }
    const block = findProposalBlock(store.find(turnId), toolUseId);
    if (!block || proposalStatus(block) !== TeamAssistV2ProposalStatus.pending) {
      return;
    }
    this.context.analytics.track('Team Assist Proposed Action Executed', {
      customerId,
      teamAssistVersion: 'v2',
      toolName: block.name,
    });

    const controller = startRequest(customerId);
    const followUp = new StreamingTeamAssistV2FollowUp({ store, signal: controller.signal, turnId, toolUseId, input });

    return this.context.gateways.teamAssistV2
      .streamProposalRun({ toolUseId, input, signal: controller.signal, onEvent: event => followUp.apply(event) })
      .then(() => this.onStreamClosed(customerId, followUp, controller.signal))
      .catch(err => this.onStreamFailed(customerId, followUp, controller.signal, err))
      .finally(() => finishRequest(customerId, controller));
  }

  onStreamClosed(customerId, followUp, signal) {
    if (this.reloadAfterFailedRun(customerId, followUp, signal) || !followUp.live) {
      return;
    }
    if (followUp.hasRun) {
      followUp.fail(TeamAssistV2ErrorCode.interrupted);
      return;
    }
    followUp.stop();
    this.reload(customerId);
  }

  onStreamFailed(customerId, followUp, signal, err) {
    if (this.reloadAfterFailedRun(customerId, followUp, signal) || !followUp.live) {
      return;
    }
    if (followUp.hasRun) {
      followUp.fail(failureCode(err));
      handleTeamAssistV2Error(this.context, err);
      return;
    }

    const code = refusalCode(err);
    if (code === INVALID_INPUT) {
      followUp.refuse({ issues: (err.errors[0].meta && err.errors[0].meta.issues) || [] });
      return;
    }
    if (CARD_REFUSALS.includes(code)) {
      followUp.refuse({ refusal: code });
      return;
    }
    if (code === ALREADY_DECIDED) {
      followUp.decided(decisionOf(err.errors[0].meta));
      this.showError(i18n.t("This action was already run or rejected, so it didn't run again."));
    } else if (code === SESSION_ENDED) {
      followUp.refuse();
      this.showError(i18n.t('This session was cleared, so nothing ran. Ask Team Assist again to start a new one.'));
    } else if (err instanceof NotExistError) {
      followUp.refuse();
      this.showError(i18n.t("This action isn't available anymore, so nothing ran. Ask Team Assist what to do next."));
    } else if (isUnanswered(err)) {
      // The run may have started before the connection broke.
      followUp.stop();
      handleTeamAssistV2Error(this.context, err, { isInteractive: true });
    } else {
      followUp.refuse();
      handleTeamAssistV2Error(this.context, err, { isInteractive: true });
    }
    this.reload(customerId);
  }

  // Whether the stream is done with: aborted, or glados's error ended the run
  // before its outcome, when the session loads again to show what's stored.
  reloadAfterFailedRun(customerId, followUp, signal) {
    if (signal.aborted) {
      return true;
    }
    if (!followUp.failedBeforeRun) {
      return false;
    }
    this.reload(customerId);
    return true;
  }

  showError(message) {
    this.context.executeAction(ShowToast, { type: ToastType.ERROR, message });
  }

  // glados's rows say what became of the proposal.
  reload(customerId) {
    this.context.executeAction(RequestTeamAssistV2, { customerId, isRefreshing: true });
  }
}

const INVALID_INPUT = 'invalid_input';
const ALREADY_DECIDED = 'already_decided';
const SESSION_ENDED = 'session_ended';
// Nothing ran and nothing changed, so the card says why and keeps Execute.
const CARD_REFUSALS = ['action_unavailable', 'apps_unavailable'];

// glados's code for a run it refused, as a 4xx's error or a 5xx's body.
function refusalCode(err) {
  if (err instanceof DomainError && err.errors.length) {
    return err.errors[0].code;
  }
  return err instanceof ServerError && err.response ? err.response.error : null;
}

// What glados says became of an already-decided proposal.
function decisionOf(meta) {
  return meta && meta.outcome === 'rejected'
    ? TeamAssistV2ProposalDecision.rejected
    : TeamAssistV2ProposalDecision.approved;
}

function isUnanswered(err) {
  return err instanceof NetworkError || err instanceof RequestTimeout;
}

function failureCode(err) {
  if (err instanceof RequestTimeout) {
    return TeamAssistV2ErrorCode.interrupted;
  }
  return err instanceof NetworkError ? TeamAssistV2ErrorCode.network : TeamAssistV2ErrorCode.requestFailed;
}
